import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { SelectSearchBase } from '../fi-select-search/select-search-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isDefined } from '@fi-shared/utils';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { flatten } from '@uirouter/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FiMenuFoldableItemComponent } from '@fi-shared/fi-menu-foldable-multi-select-item-search/fi-menu-foldable-item/fi-menu-foldable-item.component';
import { FiMenuMultiSelectItemSearchHelperService } from '@fi-shared/fi-menu-multi-select-item-search/fi-menu-multi-select-item-search-helper.service';

@Component({
    selector: 'fi-menu-foldable-multi-select-item-search',
    templateUrl: 'fi-menu-foldable-multi-select-item-search.component.html',
    styleUrls: [
        './fi-menu-foldable-multi-select-item-search.component.scss',
        '../fi-menu-multi-select-item-search/fi-menu-multi-select-item-search.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-menu-foldable-multi-select-item-search' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiMenuFoldableMultiSelectItemSearchComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiMenuFoldableMultiSelectItemSearchComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiMenuFoldableMultiSelectItemSearchComponent<T extends { Id: number; ChildItems: T[] }, TRes = number>
    extends SelectSearchBase<T, number[]>
    implements OnInit
{
    @Input() items: T[] = [];
    @Output() itemSelectionChange = new EventEmitter<T>();
    @Input() displayValue: string = 'Name';

    @Input() selectedChildItems: number[] = [];
    @Output() selectedChildItemsChange = new EventEmitter<number[]>();

    @Input() isMultiSelect: boolean = true;
    @Input() isSelectAllFeatureEnabled: boolean;
    @Input() isMainItemToggleHidden: boolean;
    @Input() isSubMenusLayout: boolean;
    @Input() isSubmenuItemSelectionRequired: boolean;
    @Input() isSubmenuItemSelectionRequiredFn: (item: T) => boolean;
    @Input() submenuItemTooltipText: string;
    @Input() subMenuHeading: string;
    @Input() externalFilterFn: FiDropdownItemDisabledFn<T>;
    @Input() getMainItemExternalDisabledFn: FiDropdownItemDisabledFn<T>;
    @Input() externalItemDisabledFn: FiDropdownItemDisabledFn<T>;
    @Input() preventDisableChildItem: boolean;
    @Input() preventDisabledMainItem: boolean;
    @Input() disableAllChildrenIfParentDisabled: boolean;
    @Input() itemsDisabledTooltipText: string;
    @Input() isSearchInputHidden = false;
    @Input() isSearchAmongChildren: boolean;
    @Input() selectAllChildrenOnMainSelect: boolean;
    @Output() onMainItemDeselection = new EventEmitter<number>();

    private readonly fiMenuMultiSelectItemSearchHelperService = inject(FiMenuMultiSelectItemSearchHelperService);

    //#region debug
    protected getDebugMain() {
        const allSelectedItems = this.items.filter((item) => this.ngModel.includes(item.Id));
        const allSelectedItemsStr = allSelectedItems?.map((item) => `${item['Name']}(${item.Id})`).join(', ');
        return allSelectedItemsStr;
    }
    protected getDebugChildItems() {
        const allChildItems: T[] = flatten(this.items.map((item) => item.ChildItems));
        const allSelectedChildItems = allChildItems?.filter((childItem) =>
            this.selectedChildItems?.includes(childItem.Id),
        );
        const allSelectedChildItemsStr = allSelectedChildItems?.map((item) => `${item['Name']}(${item.Id})`).join(', ');
        return allSelectedChildItemsStr;
    }
    //#endregion

    ngOnInit() {
        super.ngOnInit();
    }

    /*getSelectedChildItems(item: T): number[] {
        return item.ChildItems?.map((childItem: T) => childItem.Id).filter((childItemId: number) =>
            this.selectedChildItems?.includes(childItemId),
        );
    }*/

    protected getSelectedItemsFromModel(): T[] {
        return !isDefined(this.ngModel) ? [] : this.getFilteredItems();
    }

    protected updateFilteredItems() {
        this.filteredItems$.next(this.getFilteredItems());
    }

    private getFilteredItems(): T[] {
        return this.fiMenuMultiSelectItemSearchHelperService.getFilteredItems({
            items: this.items,
            searchText: this.searchText,
            displayValue: this.displayValue,
            externalFilterFn: this.externalFilterFn,
            isSearchAmongChildren: this.isSearchAmongChildren,
        });
    }

    private getFilteredChildItems(item: T): T[] {
        // here we should filter child items only by externalFilterFn
        return this.fiMenuMultiSelectItemSearchHelperService.getFilteredItems({
            items: item.ChildItems,
            searchText: undefined,
            displayValue: this.displayValue,
            externalFilterFn: this.externalFilterFn,
            isSearchAmongChildren: this.isSearchAmongChildren,
        });
    }

    private getSelectedItemChildrenIds(item: T): number[] {
        return item.ChildItems.filter((childItem) => this.selectedChildItems?.includes(childItem.Id)).map(
            (childItem) => childItem.Id,
        );
    }

    onSelectedChildItemChanged(item: T) {
        if (!this.isMultiSelect) {
            this.ngModel = [item.Id];
            this.doPropagateChanges();
        }
        const selectedItemChildrenIds: number[] = this.getSelectedItemChildrenIds(item);
        if (this.isMainItemSelectionChanged(selectedItemChildrenIds, item)) {
            this.onMainItemSelectionChange(item);
        }
        this.selectedChildItemsChange.emit(this.selectedChildItems);
    }

    private isMainItemSelectionChanged(selectedChildrenIds: number[], item: T): boolean {
        return (
            !this.preventDisableChildItem && !this.isItemSelected(item) && !!selectedChildrenIds.length
            /*|| this.isItemSelected(item) && !selectedChildrenIds.length */
        );
    }

    private handleParentSelectionChange(item: T) {
        const selectedItemIndex: number = this.ngModel?.indexOf(item.Id);
        if (selectedItemIndex === -1) {
            // new item selected, if single select mode - clear previous selection
            if (this.isMultiSelect) {
                this.ngModel.push(item.Id);
            } else {
                this.ngModel = [item.Id];
                this.selectedChildItems.length = 0;
                this.selectedChildItemsChange.emit(this.selectedChildItems);
            }
        } else {
            this.ngModel.splice(selectedItemIndex, 1);
            this.onMainItemDeselection.emit(item.Id);
        }
        this.doPropagateChanges();
    }

    private handeChildrenSelectionOnParentSelection(item: T) {
        const itemChildrenIds: number[] = this.getFilteredChildItems(item).map((childItem: T) => childItem.Id);
        const isAllChildrenSelected: boolean = itemChildrenIds.every((childId: number) =>
            this.selectedChildItems?.includes(childId),
        );
        isAllChildrenSelected ? this.deselectAllItemChildren(item) : this.selectAllItemChildren(item);
        this.selectedChildItemsChange.emit(this.selectedChildItems);
    }

    onMainItemSelectionChange(item: T) {
        if (this.selectAllChildrenOnMainSelect && !!item.ChildItems?.length) {
            this.handeChildrenSelectionOnParentSelection(item);
            return;
        }

        this.handleParentSelectionChange(item);
    }

    private selectAllItemChildren(item: T) {
        this.getFilteredChildItems(item).forEach((childItem: T) => {
            if (this.selectedChildItems.indexOf(childItem.Id) === -1) {
                this.selectedChildItems.push(childItem.Id);
            }
        });
    }

    private deselectAllItemChildren(item: T) {
        this.getFilteredChildItems(item).forEach((childItem: T) => {
            const selectedChildItemIndex: number = this.selectedChildItems.indexOf(childItem.Id);
            if (selectedChildItemIndex !== -1) {
                this.selectedChildItems.splice(selectedChildItemIndex, 1);
            }
        });
    }

    isParentItemDisabled(item: T): boolean {
        return !this.preventDisabledMainItem && this.ngModel.length === 1 && this.isItemSelected(item);
    }

    isParentInaccessible(item: T): boolean {
        return (
            this.disableAllChildrenIfParentDisabled &&
            isDefined(this.getMainItemExternalDisabledFn) &&
            this.getMainItemExternalDisabledFn(item, [])
        );
    }

    isItemSelected(item: T): boolean {
        return this.ngModel.includes(item.Id);
    }

    getIsSubmenuItemSelectionRequired(item: T) {
        return this.isSubmenuItemSelectionRequiredFn
            ? this.isSubmenuItemSelectionRequiredFn(item)
            : this.isSubmenuItemSelectionRequired;
    }

    @ViewChildren('foldableItemComponent') foldableItemComponents: QueryList<FiMenuFoldableItemComponent<T>>;
    onMatMenuOpen(trigger: MatMenuTrigger) {
        this.closeOpenedMatMenus();
        trigger.openMenu();
    }

    closeOpenedMatMenus(): void {
        this.foldableItemComponents.forEach((foldableItemComponent: FiMenuFoldableItemComponent<T>) =>
            foldableItemComponent.closeMenu(),
        );
    }
}
